import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, computed, effect, inject, signal } from '@angular/core';
import { OperationsApiService } from '../../../core/api';
import type { CardApiContract } from '../../../core/api';
import { CardDetailsApiService } from '../../cards/api/card-details-api.service';
import { CardFieldValuesApiService } from '../../cards/api/card-field-values-api.service';
import { CardsDataService } from '../../cards/services/cards-data.service';
import { CompanyUsersService } from '../../../core/services/company-users.service';
import { TenantService } from '../../../core/tenant/tenant.service';
import { WorkflowsStore } from '../stores/workflows.store';
import { CustomFieldsDataService } from '../../custom-fields/services/custom-fields-data.service';
import type { CustomFieldViewModel } from '../../custom-fields/models/custom-field.model';
import { formatFormulaResultForDisplay } from '../../custom-fields/models/formula-field.model';
import { formatRatingForQuickView, normalizeRatingValue } from '../../custom-fields/utils/rating-field.util';
import {
  formatNumberFieldForDisplayPtBr,
  parseFlexibleNumberString,
  resolveNumberFieldDecimalPlaces,
  roundToDecimalPlaces,
  shouldPreserveLeadingZeroIntegerRaw,
} from '../../custom-fields/utils/number-field.util';
import { formatFileFieldValueForList } from '../../../shared/utils';
import type {
  ItemPriority,
  ItemStatus,
  WorkflowItem,
  WorkflowItemCreate,
  WorkflowQuickField,
  WorkflowItemUpdate,
} from '../models/workflow-item.model';

const ASSIGNEES = ['Ricardo', 'Maria', 'Joao', 'Ana'];
const PRIORITIES: ItemPriority[] = ['Baixa', 'Média', 'Alta'];

/** Igual a `CardDetailsStore.MOVE_APPEND_END`: a API faz clamp ao fim da coluna. */
const MOVE_CARD_APPEND_END = 1_000_000;

const MOVE_RETRY_DELAY_MS = 400;
const MOVE_STALE_RELOAD_GRACE_MS = 30_000;
const MOVE_SETTLE_RELOAD_DELAYS_MS = [1_500, 4_000, 8_000] as const;

type JsonRecord = Record<string, unknown>;

interface PendingStageMove {
  workflowId: string;
  status: ItemStatus;
  stageId: string;
  expiresAt: number;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryableMoveHttpError(err: unknown): boolean {
  return (
    err instanceof HttpErrorResponse &&
    [0, 400, 409, 500, 502, 503, 504].includes(err.status)
  );
}

@Injectable({ providedIn: 'root' })
export class WorkflowItemsService {
  private readonly api = inject(OperationsApiService);
  private readonly cardDetailsApi = inject(CardDetailsApiService);
  private readonly injector = inject(Injector);
  private readonly companyUsers = inject(CompanyUsersService);
  private readonly tenant = inject(TenantService);
  private readonly workflowsStore = inject(WorkflowsStore);
  private readonly customFieldsData = inject(CustomFieldsDataService);
  private readonly cardFieldValuesApi = inject(CardFieldValuesApiService);
  private readonly itemsSignal = signal<WorkflowItem[]>([]);
  private readonly loadedWorkflowIdsSignal = signal<Set<string>>(new Set());
  private readonly stagesByWorkflowSignal = signal<Map<string, { id: string; name: string }[]>>(new Map());
  private readonly loadedCompanyIdSignal = signal<string | null>(null);
  private readonly pendingStageMoves = new Map<string, PendingStageMove>();
  private readonly workflowLoadRequestSeq = new Map<string, number>();

  readonly items = this.itemsSignal.asReadonly();
  readonly totalCount = computed(() => this.items().length);

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.reset();
        return;
      }
      if (this.loadedCompanyIdSignal() === companyId) return;
      this.reset();
      this.loadedCompanyIdSignal.set(companyId);
    });
  }

  getById(itemId: string | null): WorkflowItem | null {
    if (!itemId) return null;
    return this.items().find((item) => item.id === itemId) ?? null;
  }

  updateLatestComment(cardId: string, content: string | null): void {
    this.itemsSignal.update(items => items.map(item =>
      item.id === cardId ? { ...item, latestComment: content } : item));
  }

  getByWorkflow(workflowId: string): WorkflowItem[] {
    return this.items().filter((item) => item.workflowId === workflowId);
  }

  /** Força novo GET dos cards do workflow (ex.: após move — posições 1..N recalculadas na API). */
  async reloadWorkflowCards(workflowId: string): Promise<void> {
    this.loadedWorkflowIdsSignal.update((ids) => {
      if (!ids.has(workflowId)) return ids;
      const next = new Set(ids);
      next.delete(workflowId);
      return next;
    });
    await this.ensureWorkflowLoaded(workflowId);
  }

  async ensureWorkflowLoaded(workflowId: string): Promise<void> {
    const loaded = this.loadedWorkflowIdsSignal();
    if (loaded.has(workflowId)) return;

    const requestSeq = (this.workflowLoadRequestSeq.get(workflowId) ?? 0) + 1;
    this.workflowLoadRequestSeq.set(workflowId, requestSeq);

    await this.companyUsers.ensureLoaded();

    const [cards, stages] = await Promise.all([
      this.api.getCardsByWorkflow(workflowId),
      this.api.getWorkflowStages(workflowId),
    ]);

    if (this.workflowLoadRequestSeq.get(workflowId) !== requestSeq) return;

    this.stagesByWorkflowSignal.update((current) => {
      const next = new Map(current);
      next.set(
        workflowId,
        stages.map((stage) => ({ id: stage.id, name: stage.name }))
      );
      return next;
    });
    const mapped = cards.map((card) => this.reconcilePendingStageMove(this.toWorkflowItem(card), card));
    this.itemsSignal.update((items) => [
      ...items.filter((item) => item.workflowId !== workflowId),
      ...mapped,
    ]);
    this.loadedWorkflowIdsSignal.update((current) => new Set([...current, workflowId]));
  }

  /**
   * Sincroniza só as etapas do workflow com a API (sem recarregar cards).
   * Evita `targetWorkflowStageId` vindo do merge com store defasada — cenário típico de 500 intermitente no move.
   */
  private async refreshWorkflowStagesOnly(workflowId: string): Promise<void> {
    const stages = await this.api.getWorkflowStages(workflowId);
    const rows = stages.map((stage) => ({ id: stage.id, name: stage.name }));
    this.stagesByWorkflowSignal.update((current) => {
      const next = new Map(current);
      next.set(workflowId, rows);
      return next;
    });
  }

  /** Atualiza um card na lista com o payload completo da API (ex.: quickFields + fórmula após criar). */
  async refreshCardFromApi(cardId: string): Promise<void> {
    const prev = this.getById(cardId);
    if (!prev) return;
    const card = await this.api.getBoardCardById(cardId);
    if (!card) return;
    await this.companyUsers.ensureLoaded();
    const server = this.reconcilePendingStageMove(this.toWorkflowItem(card), card);
    const serverQf = server.quickFields ?? [];
    /**
     * GET /cards/{id} muitas vezes não envia `quickFields` (array vazio). Antes mantínhamos o estado da
     * listagem e o card nunca refletia edições (ex.: telefone). Preferimos reconstruir via valores gravados.
     */
    let keepLocalQuick =
      serverQf.length === 0 && Array.isArray(prev.quickFields) && prev.quickFields.length > 0;
    let effectiveQuickFields = serverQf;
    let effectiveCustomFields = server.customFields ?? {};
    if (keepLocalQuick) {
      const rebuilt = await this.rebuildQuickFieldsFromCardFieldValues(cardId, prev);
      if (rebuilt.quickFields.length > 0) {
        keepLocalQuick = false;
        effectiveQuickFields = rebuilt.quickFields;
        effectiveCustomFields = rebuilt.customFields;
      } else if (Object.keys(rebuilt.customFields).length > 0) {
        /**
         * Sem campos `showInQuickView`, ainda assim preservamos o mapa completo para as colunas
         * personalizadas da lista (ex.: datas) refletirem a edição sem precisar de novo reload.
         */
        effectiveCustomFields = rebuilt.customFields;
      }
    }
    const serverDesc = (server.description ?? '').trim();
    const serverOwnerId = (server.assigneeUserId ?? '').trim();
    const serverOwnerName = (server.assigneeName ?? '').trim();
    const prevOwnerId = (prev.assigneeUserId ?? '').trim();
    const prevOwnerName = (prev.assigneeName ?? '').trim();
    /**
     * GET por id pode omitir dono (payload parcial). Só reutilizamos o estado local quando a API não
     * envia nem id nem nome — se vier só `ownerUserId`, não misturamos o nome antigo com o novo id.
     */
    const usePrevOwner = !serverOwnerId && !serverOwnerName;
    const assigneeUserId = usePrevOwner ? prevOwnerId || null : serverOwnerId || null;
    const assigneeName = usePrevOwner ? prevOwnerName : serverOwnerName;
    this.itemsSignal.update((items) =>
      items.map((item) => {
        if (item.id !== cardId) return item;
        return {
          ...server,
          assigneeUserId,
          assigneeName,
          /** Preferir texto vindo da API; o merge antigo (`prev ?? server`) impedia preencher lista vazia. */
          description: serverDesc.length ? server.description : prev.description,
          quickFields: keepLocalQuick ? (prev.quickFields ?? []) : effectiveQuickFields,
          customFields: keepLocalQuick
            ? (prev.customFields ?? {})
            : (effectiveCustomFields ?? prev.customFields ?? {}),
        };
      })
    );
  }

  /**
   * Quando o card por id não traz quickFields, monta a visão rápida a partir de GET card-field-values
   * + schema do workflow (mesma lógica que `updateItem` com customFields). Devolve também os
   * `customFields` normalizados — não filtrados por `showInQuickView` — para a lista não perder
   * colunas como datas e textos longos após edições.
   */
  private async rebuildQuickFieldsFromCardFieldValues(
    cardId: string,
    prev: WorkflowItem
  ): Promise<{
    quickFields: WorkflowQuickField[];
    customFields: Record<string, string | number | boolean | string[] | null>;
  }> {
    try {
      const { items } = await this.cardFieldValuesApi.getByCard(cardId);
      const merged: Record<string, string | number | boolean | string[] | null> = {
        ...(prev.customFields ?? {}),
      };
      for (const row of items) {
        merged[row.workflowFieldId] = row.value;
      }
      const quickFields = this.buildQuickFieldsFromCustomValues(prev.workflowId, merged);
      const customFields = this.mergeCardFieldValues(prev.workflowId, merged, quickFields);
      return { quickFields, customFields };
    } catch {
      return { quickFields: [], customFields: {} };
    }
  }

  async deleteCards(cardIds: string[]): Promise<void> {
    const companyId = this.tenant.companyId();
    const ids = new Set(cardIds);
    const workflowIds = new Set(this.items().filter((item) => ids.has(item.id)).map((item) => item.workflowId));
    await this.api.deleteCards([...ids]);
    if (this.tenant.companyId() !== companyId) return;
    // Discard older list responses so they cannot restore deleted cards in the UI.
    for (const workflowId of workflowIds) {
      this.workflowLoadRequestSeq.set(workflowId, (this.workflowLoadRequestSeq.get(workflowId) ?? 0) + 1);
    }
    this.itemsSignal.update((items) => items.filter((item) => !ids.has(item.id)));
  }

  removeItem(itemId: string): void {
    this.itemsSignal.update((items) => items.filter((item) => item.id !== itemId));
  }

  patchItemLocal(
    itemId: string,
    patch: Partial<
      Pick<
        WorkflowItem,
        'title' | 'description' | 'dueDate' | 'assigneeName' | 'assigneeUserId' | 'status'
      >
    >
  ): void {
    this.itemsSignal.update((items) =>
      items.map((item) => (item.id === itemId ? { ...item, ...patch } : item))
    );
  }

  patchItemCustomFieldLocal(
    itemId: string,
    workflowFieldId: string,
    value: string | number | boolean | string[] | null
  ): void {
    const normalizedFieldId = workflowFieldId.trim().toLowerCase();
    if (!normalizedFieldId) return;

    this.itemsSignal.update((items) =>
      items.map((item) => {
        if (item.id !== itemId) return item;

        const field = this.customFieldsData
          .getFieldsByWorkflow(item.workflowId)
          .find(
            (candidate) =>
              candidate.fieldId.trim().toLowerCase() === normalizedFieldId ||
              this.normalizeKey(candidate.key || candidate.name) ===
                this.normalizeKey(workflowFieldId)
          );
        const fieldId = field?.fieldId?.trim() || workflowFieldId.trim();
        const fieldKey = this.normalizeKey(field?.key || field?.name || workflowFieldId);
        const nextCustomFields = { ...(item.customFields ?? {}) };

        for (const key of Object.keys(nextCustomFields)) {
          const sameFieldId = key.trim().toLowerCase() === fieldId.trim().toLowerCase();
          const sameFieldKey = fieldKey.length > 0 && this.normalizeKey(key) === fieldKey;
          if (sameFieldId || sameFieldKey) delete nextCustomFields[key];
        }

        const hasValue =
          value != null &&
          !(typeof value === 'string' && value.trim().length === 0) &&
          !(Array.isArray(value) && value.length === 0);
        if (hasValue) {
          nextCustomFields[fieldId] = value;
          if (fieldKey) nextCustomFields[fieldKey] = value;
        }

        return {
          ...item,
          customFields: nextCustomFields,
          quickFields: this.buildQuickFieldsFromCustomValues(item.workflowId, nextCustomFields),
        };
      })
    );
  }

  /**
   * Move no board/lista. Retorna false se não achou card, etapa ou a API falhou.
   * Usa posição “fim da coluna” (valor alto + clamp na API), como em detalhes do card.
   * Antes do move, atualiza etapas pelo GET /stages para o id da coluna bater com o servidor.
   * Em 5xx transitório, repete após pequeno atraso (uma vez).
   */
  async moveItemToStatus(itemId: string, status: ItemStatus): Promise<boolean> {
    const current = this.getById(itemId);
    if (!current) return false;

    const workflowId = current.workflowId;
    await this.refreshWorkflowStagesOnly(workflowId);

    const stageId = this.mapStatusToStageId(workflowId, status);
    if (!stageId.trim()) return false;

    const prevStatus = current.status;
    this.registerPendingStageMove(itemId, workflowId, status, stageId);

    this.itemsSignal.update((items) =>
      items.map((item) => (item.id === itemId ? { ...item, status } : item))
    );

    const sendMove = async (sid: string): Promise<void> => {
      await this.api.moveCard(itemId, {
        workflowStageId: sid,
        targetPosition: MOVE_CARD_APPEND_END,
      });
    };

    try {
      await sendMove(stageId);
      return this.finishAcceptedMove(itemId, workflowId);
    } catch (firstErr) {
      if (!isRetryableMoveHttpError(firstErr)) {
        return this.rollbackMoveIfServerDidNotAccept(
          itemId,
          workflowId,
          status,
          stageId,
          prevStatus
        );
      }
      await delay(MOVE_RETRY_DELAY_MS);
      let retryStageId = stageId;
      try {
        await this.refreshWorkflowStagesOnly(workflowId);
        const sid2 = this.mapStatusToStageId(workflowId, status);
        retryStageId = sid2;
        if (!sid2.trim()) {
          this.clearPendingStageMove(itemId, stageId);
          this.revertItemStatus(itemId, prevStatus);
          return false;
        }
        this.registerPendingStageMove(itemId, workflowId, status, sid2);
        await sendMove(sid2);
        return this.finishAcceptedMove(itemId, workflowId);
      } catch {
        return this.rollbackMoveIfServerDidNotAccept(
          itemId,
          workflowId,
          status,
          retryStageId,
          prevStatus
        );
      }
    }
  }

  private revertItemStatus(itemId: string, status: ItemStatus): void {
    this.itemsSignal.update((items) =>
      items.map((item) => (item.id === itemId ? { ...item, status } : item))
    );
  }

  async addItem(workflowId: string, projectId: string, dto: WorkflowItemCreate): Promise<WorkflowItem> {
    const effectiveDueDate = this.resolveDueDate(dto.dueDate, workflowId);
    const payload: JsonRecord = {
      title: dto.title,
      description: dto.description ?? null,
      workflowId,
      workflowStageId: this.mapStatusToStageId(workflowId, dto.status ?? 'Backlog'),
      dueDate: effectiveDueDate,
      priority: dto.priority === 'Média' ? 'Media' : dto.priority,
    };
    const aid = dto.assigneeId?.trim();
    if (aid) {
      payload['ownerUserId'] = aid;
    }
    if (dto.createFieldValues?.length) {
      payload['fieldValues'] = dto.createFieldValues;
    }

    const created = await this.api.createCard(payload);
    const cardId = this.api.parseCreatedCardId(created) || `card-${Date.now()}`;
    const codeFromApi = this.api.parseCreatedCardCode(created);
    /**
     * `dto.customFields` chega indexado por workflowFieldId (UUID). A lista do workflow lê os valores
     * por key normalizada (ex.: `data_indicacao`); sem o merge abaixo, campos sem `showInQuickView`
     * só apareceriam após o próximo `GET /api/cards/workflow/{id}`.
     */
    const quickFieldsBuilt = this.buildQuickFieldsFromCustomValues(workflowId, dto.customFields ?? {});
    const newItem: WorkflowItem = {
      id: cardId,
      code: codeFromApi,
      title: dto.title,
      description: dto.description,
      status: dto.status ?? 'Backlog',
      assigneeName: dto.assigneeName ?? this.resolveAssigneeName(dto.assigneeId) ?? '',
      assigneeUserId: aid ?? null,
      priority: dto.priority,
      dueDate: effectiveDueDate,
      workflowId,
      projectId,
      createdAt: new Date().toISOString(),
      customFields: this.mergeCardFieldValues(workflowId, dto.customFields ?? {}, quickFieldsBuilt),
      quickFields: quickFieldsBuilt,
    };

    this.itemsSignal.update((items) => [...items, newItem]);
    return newItem;
  }

  async updateItem(itemId: string, dto: WorkflowItemUpdate): Promise<WorkflowItem | null> {
    const current = this.getById(itemId);
    if (!current) return null;

    const previous = { ...current };

    const nextQuickFields =
      dto.customFields != null
        ? this.buildQuickFieldsFromCustomValues(current.workflowId, dto.customFields)
        : current.quickFields ?? [];
    const updated: WorkflowItem = {
      ...current,
      ...dto,
      customFields:
        dto.customFields != null
          ? this.mergeCardFieldValues(current.workflowId, dto.customFields, nextQuickFields)
          : current.customFields ?? {},
      quickFields: nextQuickFields,
    };
    if (dto.assigneeName !== undefined) {
      const n = (updated.assigneeName ?? '').trim();
      updated.assigneeUserId = n ? this.companyUsers.resolveUserIdByDisplayName(n) : null;
    }

    this.itemsSignal.update((items) =>
      items.map((item) => (item.id === itemId ? updated : item))
    );

    const payload: JsonRecord = {
      title: updated.title,
      description: updated.description ?? null,
      workflowId: updated.workflowId,
      workflowStageId: this.mapStatusToStageId(updated.workflowId, updated.status),
      dueDate: updated.dueDate,
      priority: updated.priority === 'Média' ? 'Media' : updated.priority,
    };
    /** Dono: `PUT /cards/{id}` pode não aplicar `ownerUserId`; usamos `PUT .../owner` (igual ao detalhe). */
    if (dto.assigneeName === undefined) {
      payload['ownerName'] = updated.assigneeName ?? null;
    }

    try {
      await this.api.updateCard(itemId, payload);

      if (dto.assigneeName !== undefined) {
        await this.cardDetailsApi.updateOwner(itemId, {
          ownerUserId: (updated.assigneeUserId ?? '').trim() || null,
        });
      }

      if (dto.customFields != null) {
        const cardsData = this.injector.get(CardsDataService);
        await cardsData.syncInlineCustomFieldMap(
          itemId,
          current.workflowId,
          updated.customFields ?? {}
        );
      }

      return updated;
    } catch {
      this.itemsSignal.update((items) =>
        items.map((item) => (item.id === itemId ? previous : item))
      );
      return null;
    }
  }

  countOverdue(items: WorkflowItem[]): number {
    const today = new Date().toISOString().slice(0, 10);
    return items.filter((item) => item.dueDate != null && item.dueDate < today).length;
  }

  getAssignees(): { id: string; name: string }[] {
    return ASSIGNEES.map((name, index) => ({ id: String(index), name }));
  }

  getPriorities(): ItemPriority[] {
    return [...PRIORITIES];
  }

  getStatuses(): ItemStatus[] {
    return ['Backlog', 'Em andamento', 'Feito'];
  }

  reset(): void {
    this.itemsSignal.set([]);
    this.loadedWorkflowIdsSignal.set(new Set());
    this.stagesByWorkflowSignal.set(new Map());
    this.loadedCompanyIdSignal.set(null);
    this.pendingStageMoves.clear();
    this.workflowLoadRequestSeq.clear();
  }

  private registerPendingStageMove(
    itemId: string,
    workflowId: string,
    status: ItemStatus,
    stageId: string
  ): void {
    const expiresAt = Date.now() + MOVE_STALE_RELOAD_GRACE_MS;
    this.pendingStageMoves.set(itemId, {
      workflowId,
      status,
      stageId,
      expiresAt,
    });
    globalThis.setTimeout(() => {
      const pending = this.pendingStageMoves.get(itemId);
      if (
        pending?.workflowId === workflowId &&
        pending.stageId.trim().toLowerCase() === stageId.trim().toLowerCase() &&
        pending.expiresAt <= Date.now()
      ) {
        this.pendingStageMoves.delete(itemId);
      }
    }, MOVE_STALE_RELOAD_GRACE_MS + 500);
  }

  private async serverHasMoveTarget(
    itemId: string,
    workflowId: string,
    status: ItemStatus,
    stageId: string
  ): Promise<boolean> {
    try {
      const card = await this.api.getBoardCardById(itemId);
      if (!card || card.workflowId !== workflowId) return false;
      const serverStageId = card.workflowStageId.trim().toLowerCase();
      const targetStageId = stageId.trim().toLowerCase();
      if (serverStageId && serverStageId === targetStageId) return true;
      const mappedStatus = this.mapStageIdToStatus(workflowId, card.workflowStageId);
      return this.normalizeStageLabel(mappedStatus) === this.normalizeStageLabel(status);
    } catch {
      return false;
    }
  }

  private async finishAcceptedMove(itemId: string, workflowId: string): Promise<boolean> {
    await this.reloadWorkflowCardsBestEffort(workflowId);
    this.scheduleMoveSettleReloads(itemId, workflowId);
    return true;
  }

  private async rollbackMoveIfServerDidNotAccept(
    itemId: string,
    workflowId: string,
    status: ItemStatus,
    stageId: string,
    prevStatus: ItemStatus
  ): Promise<boolean> {
    if (await this.serverHasMoveTarget(itemId, workflowId, status, stageId)) {
      return this.finishAcceptedMove(itemId, workflowId);
    }
    this.clearPendingStageMove(itemId, stageId);
    this.revertItemStatus(itemId, prevStatus);
    return false;
  }

  private clearPendingStageMove(itemId: string, expectedStageId?: string): void {
    if (!expectedStageId) {
      this.pendingStageMoves.delete(itemId);
      return;
    }
    const pending = this.pendingStageMoves.get(itemId);
    if (pending?.stageId.trim().toLowerCase() === expectedStageId.trim().toLowerCase()) {
      this.pendingStageMoves.delete(itemId);
    }
  }

  private async reloadWorkflowCardsBestEffort(workflowId: string): Promise<void> {
    try {
      await this.reloadWorkflowCards(workflowId);
    } catch {
      // A delayed settle reload will try again without undoing the accepted move.
    }
  }

  private reconcilePendingStageMove(item: WorkflowItem, card: CardApiContract): WorkflowItem {
    const pending = this.pendingStageMoves.get(item.id);
    if (!pending) return item;

    if (pending.workflowId !== item.workflowId || Date.now() > pending.expiresAt) {
      this.pendingStageMoves.delete(item.id);
      return item;
    }

    const serverStageId = card.workflowStageId.trim().toLowerCase();
    const pendingStageId = pending.stageId.trim().toLowerCase();
    const serverReachedTarget =
      (!!serverStageId && serverStageId === pendingStageId) ||
      this.normalizeStageLabel(item.status) === this.normalizeStageLabel(pending.status);

    if (serverReachedTarget) {
      return this.normalizeStageLabel(item.status) === this.normalizeStageLabel(pending.status)
        ? item
        : { ...item, status: pending.status };
    }

    return { ...item, status: pending.status };
  }

  private scheduleMoveSettleReloads(itemId: string, workflowId: string): void {
    for (const delayMs of MOVE_SETTLE_RELOAD_DELAYS_MS) {
      globalThis.setTimeout(() => {
        const pending = this.pendingStageMoves.get(itemId);
        if (!pending || pending.workflowId !== workflowId) return;
        void this.reloadWorkflowCards(workflowId).catch(() => undefined);
      }, delayMs);
    }
  }

  private toWorkflowItem(card: {
    id: string;
    code?: string | null;
    workflowId: string;
    projectId: string;
    title: string;
    description?: string | null;
    latestComment?: string | null;
    ownerName: string;
    ownerUserId?: string | null;
    priority: string | null;
    dueDate: string | null;
    workflowStageId: string;
    position?: number | null;
    createdAt?: string | null;
    quickFields: WorkflowQuickField[];
    fieldValues?: Record<string, string | number | boolean | string[] | null>;
  }): WorkflowItem {
    const nameFromApi = (card.ownerName ?? '').trim();
    const oid = (card.ownerUserId ?? '').trim();
    const fromDirectory = oid ? this.companyUsers.resolveUserDisplayName(oid).trim() : '';
    const assigneeName =
      nameFromApi ||
      (fromDirectory && fromDirectory !== oid ? fromDirectory : '') ||
      '';
    let desc = (card.description ?? '').trim();
    if (!desc.length) {
      desc = this.descriptionFromQuickFieldsFallback(card.quickFields ?? []);
    }
    return {
      id: card.id,
      code: card.code ?? null,
      title: card.title,
      description: desc.length ? desc : undefined,
      latestComment: card.latestComment ?? null,
      status: this.mapStageIdToStatus(card.workflowId, card.workflowStageId),
      position: card.position ?? null,
      createdAt: card.createdAt ?? null,
      assigneeName,
      assigneeUserId: oid || null,
      priority: this.normalizePriority(card.priority),
      dueDate: card.dueDate,
      workflowId: card.workflowId,
      projectId: card.projectId,
      customFields: this.mergeCardFieldValues(
        card.workflowId,
        card.fieldValues ?? {},
        card.quickFields ?? []
      ),
      quickFields: card.quickFields ?? [],
    };
  }

  /**
   * Une os valores que o backend manda em `fieldValues` (todos os campos, indexados por UUID / key /
   * label) com o mapa derivado dos `quickFields` (que costuma trazer labels amigáveis já prontos).
   * Precedência:
   *   1. Valor do quickField — preserva formatação feita pelo backend (ex.: "ABR" em select).
   *   2. Valor de `fieldValues` — cobre campos não retornados em quickFields, para que a lista
   *      respeite apenas o flag WEB de exibição do campo.
   */
  private mergeCardFieldValues(
    workflowId: string,
    rawFieldValues: Record<string, string | number | boolean | string[] | null>,
    quickFields: WorkflowQuickField[]
  ): Record<string, string | number | boolean | string[] | null> {
    const normalized: Record<string, string | number | boolean | string[] | null> = {};
    const fieldIdToKey = new Map<string, string>();
    const fieldByKey = new Map<string, CustomFieldViewModel>();
    for (const field of this.customFieldsData.getFieldsByWorkflow(workflowId)) {
      const key = this.normalizeKey(field.key || field.name);
      if (!key) continue;
      if (field.fieldId) fieldIdToKey.set(field.fieldId, key);
      fieldByKey.set(key, field);
    }

    for (const [rawKey, rawValue] of Object.entries(rawFieldValues)) {
      if (rawValue == null) continue;
      const mappedKey = fieldIdToKey.get(rawKey) ?? this.normalizeKey(rawKey);
      if (!mappedKey) continue;
      if (normalized[mappedKey] != null) continue;
      normalized[mappedKey] = rawValue;
    }

    const fromQuick = this.toCustomFieldMapFromQuickFields(quickFields);
    for (const [key, value] of Object.entries(fromQuick)) {
      if (value == null) continue;
      if (typeof value === 'string' && value.trim().length === 0) continue;
      const field = fieldByKey.get(key);
      const keepPersistedGuid =
        (field?.type === 'PERSON' || field?.type === 'USER') && normalized[key] != null;
      if (keepPersistedGuid) continue;
      normalized[key] = value;
    }

    return normalized;
  }

  /**
   * Último fallback se `mapCard` ainda não preencheu `description` (formatos de API variados).
   */
  private descriptionFromQuickFieldsFallback(quickFields: WorkflowQuickField[]): string {
    const norm = (s: string) =>
      s
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .trim();
    for (const qf of quickFields) {
      const k = norm(qf.key ?? '');
      const lbl = norm(qf.label ?? '');
      const wid = norm(qf.workflowFieldId ?? '');
      const match =
        qf.fieldType === 10002 ||
        k === 'native:description' ||
        k === 'description' ||
        k === 'descricao' ||
        k === 'carddescription' ||
        (k.startsWith('native:') && (k.endsWith('description') || k.endsWith('descricao'))) ||
        wid === 'native:description' ||
        lbl === 'description' ||
        lbl === 'descricao';
      if (!match) continue;
      const raw = qf.value;
      if (raw == null || typeof raw === 'boolean') continue;
      const s = String(raw).trim();
      if (s.length) return s;
    }
    return '';
  }

  private toCustomFieldMapFromQuickFields(
    quickFields: WorkflowQuickField[]
  ): Record<string, string | number | boolean | string[] | null> {
    return quickFields.reduce<Record<string, string | number | boolean | string[] | null>>((acc, field) => {
      const key = this.normalizeKey(field.key);
      if (!key) return acc;
      acc[key] = field.value as string | number | boolean | string[] | null;
      return acc;
    }, {});
  }

  private buildQuickFieldsFromCustomValues(
    workflowId: string,
    customValues: Record<string, string | number | boolean | string[] | null>
  ): WorkflowQuickField[] {
    const fields = this.customFieldsData
      .getFieldsByWorkflow(workflowId)
      .filter((field) => field.showInQuickView);
    return fields.reduce<WorkflowQuickField[]>((acc, field) => {
        const key = this.normalizeKey(field.key || field.name);
        // createCard grava customFields por workflowFieldId (UUID), não pela key normalizada
        const raw =
          customValues[field.fieldId] ?? (key ? customValues[key] : undefined) ?? null;
        if (raw == null || (typeof raw === 'string' && raw.trim().length === 0)) return acc;
        const selectedOption =
          (field.type === 'SELECT' || field.type === 'ROW_COLOR')
            ? field.options.find((option) => option.value === String(raw))
            : null;
        let normalizedValue: string | number | boolean = selectedOption
          ? selectedOption.label
          : Array.isArray(raw)
            ? raw.map((item) => String(item)).join(', ')
            : typeof raw === 'boolean' || typeof raw === 'number'
              ? raw
              : raw;
        if (field.type === 'NUMBER' || field.type === 'PROGRESS') {
          const dp = resolveNumberFieldDecimalPlaces(field, raw);
          const rawText = String(raw).trim();
          if (shouldPreserveLeadingZeroIntegerRaw(rawText, dp)) {
            normalizedValue = rawText.replace(/\s+/g, '');
          } else {
            const n =
              typeof raw === 'number' && Number.isFinite(raw)
                ? raw
                : parseFlexibleNumberString(rawText);
            if (n != null && Number.isFinite(n)) {
              const rounded = roundToDecimalPlaces(n, dp);
              normalizedValue = formatNumberFieldForDisplayPtBr(rounded, dp, {
                percentSuffix: field.numberShowPercentSuffix === true,
              });
            }
          }
        }
        if (field.type === 'FORMULA') {
          normalizedValue = formatFormulaResultForDisplay(
            normalizedValue,
            field.formulaResultDecimalPlaces ?? 0
          );
        }
        if (field.type === 'RATING') {
          const rv = normalizeRatingValue(raw, field.ratingLevels);
          if (rv == null) return acc;
          normalizedValue = formatRatingForQuickView(rv, field.ratingLevels);
        }
        if (field.type === 'FILE' && typeof raw === 'string') {
          normalizedValue = formatFileFieldValueForList(raw, 'arquivo anexado');
        }
        acc.push({
          workflowFieldId: field.fieldId,
          key,
          label: field.name,
          fieldType: this.domainTypeToApiFieldType(field.type),
          value: normalizedValue,
          colorHex: selectedOption?.colorHex ?? null,
        });
        return acc;
      }, []);
  }

  private domainTypeToApiFieldType(fieldType: CustomFieldViewModel['type']): number {
    if (fieldType === 'ROW_COLOR') return 21;
    if (fieldType === 'LONG_TEXT') return 2;
    if (fieldType === 'NUMBER') return 3;
    if (fieldType === 'DATE') return 4;
    if (fieldType === 'BOOLEAN' || fieldType === 'CHECKBOX') return 5;
    if (fieldType === 'SELECT' || fieldType === 'MULTI_SELECT' || fieldType === 'USER') return 6;
    if (fieldType === 'CURRENCY') return 7;
    if (fieldType === 'PROGRESS') return 8;
    if (fieldType === 'PERSON') return 9;
    if (fieldType === 'PHONE') return 10;
    if (fieldType === 'EMAIL') return 11;
    if (fieldType === 'FILE') return 12;
    if (fieldType === 'LABELS') return 13;
    if (fieldType === 'FORMULA') return 14;
    if (fieldType === 'RATING') return 15;
    return 1;
  }

  /**
   * Resolve o id da etapa na API a partir do nome (ou id) mostrado no board.
   * Nunca faz fallback para a primeira etapa — isso gravava o move no stage errado.
   */
  private mapStatusToStageId(workflowId: string, status: ItemStatus): string {
    const trimmed = status.trim();
    if (!trimmed) return '';

    const stages = this.getMergedStagesForWorkflow(workflowId);
    const key = this.normalizeStageLabel(status);

    const byExactId = stages.find(
      (s) => s.id.trim().length > 0 && s.id.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (byExactId?.id?.trim()) return byExactId.id.trim();

    const target = stages.find(
      (stage) =>
        this.normalizeStageLabel(stage.id) === key || this.normalizeStageLabel(stage.name) === key
    );
    if (target?.id?.trim()) return target.id.trim();

    // Coluna pode estar com o próprio id da etapa (ex.: API devolveu nome vazio no map).
    if (this.looksLikeWorkflowStageGuid(trimmed)) return trimmed;

    return '';
  }

  /**
   * Une etapas do GET /stages (cache) com as da lista de workflows (store).
   * Não exige id em cada linha: combina por nome normalizado para recuperar o id quando uma fonte veio incompleta.
   */
  private getMergedStagesForWorkflow(workflowId: string): { id: string; name: string }[] {
    const fromLoad = this.stagesByWorkflowSignal().get(workflowId) ?? [];
    const wf = this.workflowsStore.getById(workflowId);
    const fromStore = wf?.stages?.map((s) => ({ id: s.id ?? '', name: s.name ?? '' })) ?? [];
    const byNormKey = new Map<string, { id: string; name: string }>();

    const absorb = (id: string, name: string) => {
      const tid = id.trim();
      const tname = name.trim();
      if (!tid && !tname) return;

      const keys: string[] = [];
      if (tname) keys.push(this.normalizeStageLabel(tname));
      else if (tid) keys.push(this.normalizeStageLabel(tid));

      for (const nk of keys) {
        if (!nk) continue;
        const prev = byNormKey.get(nk);
        byNormKey.set(nk, {
          id: tid || prev?.id || '',
          name: tname || prev?.name || tid,
        });
      }
    };

    for (const s of fromLoad) absorb(s.id ?? '', s.name ?? '');
    for (const s of fromStore) absorb(s.id ?? '', s.name ?? '');

    const rows = Array.from(byNormKey.values());
    const byCanonId = new Map<string, { id: string; name: string }>();
    const idOnlyName = (r: { id: string; name: string }): boolean => {
      const id = r.id.trim().toLowerCase();
      const nm = r.name.trim().toLowerCase();
      return Boolean(id && nm && nm === id);
    };
    const preferStageRow = (
      cur: { id: string; name: string },
      next: { id: string; name: string }
    ): { id: string; name: string } => {
      if (idOnlyName(cur) && !idOnlyName(next)) return next;
      if (!idOnlyName(cur) && idOnlyName(next)) return cur;
      return next.name.trim().length > cur.name.trim().length ? next : cur;
    };

    const noId: { id: string; name: string }[] = [];
    for (const row of rows) {
      const idLower = row.id.trim().toLowerCase();
      if (!idLower) {
        noId.push(row);
        continue;
      }
      const prev = byCanonId.get(idLower);
      byCanonId.set(idLower, prev ? preferStageRow(prev, row) : row);
    }
    return [...Array.from(byCanonId.values()), ...noId];
  }

  private normalizeStageLabel(value: string): string {
    return value
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  }

  /** UUID v1–v8 (incl. v7); evita travar o move quando o status no board é o id da etapa. */
  private looksLikeWorkflowStageGuid(value: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());
  }

  private mapStageIdToStatus(workflowId: string, stageId: string): ItemStatus {
    const stages = this.stagesByWorkflowSignal().get(workflowId) ?? [];
    const normalizedStageId = stageId.trim().toLowerCase();
    const byId = stages.find((stage) => stage.id.trim().toLowerCase() === normalizedStageId);
    if (byId) return byId.name;

    const byName = stages.find((stage) => stage.name.trim().toLowerCase() === normalizedStageId);
    if (byName) return byName.name;

    return stageId;
  }

  private resolveAssigneeName(assigneeId?: string): string {
    if (assigneeId == null || assigneeId.trim() === '') return '';
    const n = Number(assigneeId);
    if (Number.isFinite(n) && ASSIGNEES[n] != null) return ASSIGNEES[n];
    return '';
  }

  private readString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private normalizeKey(value: string | null | undefined): string {
    return (value ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private normalizePriority(value: string | null | undefined): ItemPriority {
    const normalized = (value ?? '').trim().toLowerCase();
    if (normalized === 'alta' || normalized === 'high') return 'Alta';
    if (normalized === 'baixa' || normalized === 'low') return 'Baixa';
    return 'Média';
  }

  private resolveDueDate(manualDueDate: string | null, workflowId: string): string | null {
    const trimmedManualDueDate = (manualDueDate ?? '').trim();
    if (trimmedManualDueDate) return trimmedManualDueDate;

    const workflow = this.workflowsStore.getById(workflowId);
    const defaultDueInDays = workflow?.defaultDueInDays;
    if (typeof defaultDueInDays !== 'number' || defaultDueInDays < 0) return null;

    const utcToday = new Date();
    const dueDate = new Date(
      Date.UTC(utcToday.getUTCFullYear(), utcToday.getUTCMonth(), utcToday.getUTCDate())
    );
    dueDate.setUTCDate(dueDate.getUTCDate() + Math.trunc(defaultDueInDays));
    return dueDate.toISOString().slice(0, 10);
  }
}
